import { useMemo } from 'react';
import moment from 'moment';
import { useZone } from './ZoneContext';
import { normalizeString } from 'utils/Validations';

// Tipos de datos
interface HolidayData {
    selectedHolidays: string[];
}

interface WeekDaysData {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
}

interface ConsecutiveDaysData {
    typeConsecutive: 'consecute_days' | 'days_week';
    resetTo?: 'day_number' | 'days_week';
    dayNumber?: number;
    weekday1?: string;
    weekday2?: string;
    selectedOvertimeSustitution?: string;
}

interface DiferencialDailyData {
    start?: string;
    end?: string;
    withinZone?: string;
    withinTurn?: string;
    early?: string;
    late?: string;
    afterTimeStart?: string;
    beforeTimeStart?: string;
    afterTimeFinish?: string;
    beforeTimeFinish?: string;
}

interface DiferencialWeeklyData {
    weekday1Weekly?: string;
    weekday2Weekly?: string;
    startWeekly?: string;
    endWeekly?: string;
    withinZoneWeekly?: string;
    withinTurnWeekly?: string;
    earlyWeekly?: string;
    lateWeekly?: string;
    afterTimeStartWeekly?: string;
    beforeTimeStartWeekly?: string;
    afterTimeFinishWeekly?: string;
    beforeTimeFinishWeekly?: string;
}

interface GeneralData {
    name: string;
    description: string;
    requiredApproval: boolean;
    type: 'holiday' | 'week_days' | 'consecutive_days_worked' | 'diferential_daily_extend_zone_rule' | 'diferential_weekly_extend_daily';
}

interface Zone {
    general: GeneralData;
    holiday: HolidayData;
    weekDays: WeekDaysData;
    consecutiveDays: ConsecutiveDaysData;
    diferentialDaily: DiferencialDailyData;
    diferentialWeekly: DiferencialWeeklyData;
}

interface Row {
    id: string | number;
    name: string;
}

interface ValidationResult {
    isValid: boolean;
}

interface FormValidationResult {
    existName: boolean;
    hasChanges: boolean;
    isDisabled: boolean;
}

/** Validaciones específicas para tipo 'holiday' **/
const validateHolidayType = (holidayData: HolidayData): ValidationResult => {
    // Validar que se hayan seleccionado feriados
    return { isValid: holidayData.selectedHolidays.length !== 0 };
};

/** Validaciones específicas para tipo 'week_days' **/
const validateWeekDaysType = (weekDaysData: WeekDaysData): ValidationResult => {
    const weekdays: (keyof WeekDaysData)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    // Validar que al menos un día de la semana esté seleccionado
    const hasSelectedDay = weekdays.some(day => weekDaysData[day] === true);
    return { isValid: hasSelectedDay };
};

/**
 * Validaciones específicas para tipo 'consecutive_days_worked'
 */
const validateConsecutiveDaysType = (consecutiveDaysData: ConsecutiveDaysData): ValidationResult => {
    const errors: string[] = [];

    // Validar según el tipo de consecutivo
    if (consecutiveDaysData.typeConsecutive === 'consecute_days') {
        if (consecutiveDaysData.resetTo === 'day_number') {
            if (consecutiveDaysData.dayNumber && consecutiveDaysData.dayNumber < 0) {
                errors.push('El número de día debe ser mayor a 0');
            }
        } else if (consecutiveDaysData.resetTo === 'days_week') {
            if (!consecutiveDaysData.weekday1 || consecutiveDaysData.weekday1 === '') {
                errors.push('Debe seleccionar el día de la semana 1');
            }
        }
    } else if (consecutiveDaysData.typeConsecutive === 'days_week') {
        if (!consecutiveDaysData.weekday2 || consecutiveDaysData.weekday2 === '') {
            errors.push('Debe seleccionar el día de la semana 2');
        }
    }

    // Nota: selectedOvertimeSustitution es opcional según el contexto original

    return { isValid: errors.length === 0 };
};

/**
 * Validar que un momento sea válido y no sea el valor por defecto
 */
const isValidMomentField = (momentValue?: string): boolean => {
    if (momentValue) {
        return moment(momentValue).isValid() ? false : true;
    }
    return false;
};

/**
 * Validaciones específicas para tipo 'diferential_daily_extend_zone_rule'
 */
const validateDiferencialDailyType = (dailyData: DiferencialDailyData): ValidationResult => {
    const errors: string[] = [];
    // Validar todos los campos de tiempo
    const conditionalTimeFields = [
        dailyData.start,
        dailyData.end,
        dailyData.withinZone,
        dailyData.withinTurn,
        dailyData.early,
        dailyData.late,
        dailyData.afterTimeStart,
        dailyData.beforeTimeStart,
        dailyData.afterTimeFinish,
        dailyData.beforeTimeFinish
    ];

    conditionalTimeFields.forEach(value => {
        if (isValidMomentField(value)) {
            errors.push(`debe ser una hora válida.`);
        }
    });

    return { isValid: errors.length === 0 };
};

/**
 * Validaciones específicas para tipo 'diferential_weekly_extend_daily'
 */
const validateDiferencialWeeklyType = (weeklyData: DiferencialWeeklyData): ValidationResult => {
    const errors: string[] = [];
    // Validar días de la semana
    if (!weeklyData.weekday1Weekly || weeklyData.weekday1Weekly === '') {
        errors.push('Debe seleccionar el día de inicio semanal');
    }

    if (!weeklyData.weekday2Weekly || weeklyData.weekday2Weekly === '') {
        errors.push('Debe seleccionar el día de fin semanal');
    }

    // Validar campos condicionales de tiempo (solo si están habilitados)

    const conditionalTimeFields = [
        weeklyData.startWeekly,
        weeklyData.endWeekly,
        weeklyData.withinZoneWeekly,
        weeklyData.withinTurnWeekly,
        weeklyData.earlyWeekly,
        weeklyData.lateWeekly,
        weeklyData.afterTimeStartWeekly,
        weeklyData.beforeTimeStartWeekly,
        weeklyData.afterTimeFinishWeekly,
        weeklyData.beforeTimeFinishWeekly
    ];

    conditionalTimeFields.forEach(value => {
        if (value && isValidMomentField(value)) {
            errors.push(`debe ser una hora válida.`);
        }
    });

    return { isValid: errors.length === 0 };
};

// ============================================================================
// 2. VALIDADOR PRINCIPAL POR TIPO
// ============================================================================

/** Validar formulario según el tipo de zona seleccionado **/
const validateFormByType = (zone: Zone): ValidationResult => {
    const { general, holiday, weekDays, consecutiveDays, diferentialDaily, diferentialWeekly } = zone;

    // Ejecutar validación específica según el tipo
    switch (general.type) {
        case 'holiday':
            return validateHolidayType(holiday);

        case 'week_days':
            return validateWeekDaysType(weekDays);

        case 'consecutive_days_worked':
            return validateConsecutiveDaysType(consecutiveDays);

        case 'diferential_daily_extend_zone_rule':
            return validateDiferencialDailyType(diferentialDaily);

        case 'diferential_weekly_extend_daily':
            return validateDiferencialWeeklyType(diferentialWeekly);

        default:
            return { isValid: false };
    }
};

// Hook para validaciones del formulario
const useFormValidation = (rows: Row[], selectedRow: Row[] | null = null): FormValidationResult => {
    const zone = useZone();

    return useMemo(() => {
        const existName = (name: string): boolean => {

            if(selectedRow){
                const normalizedName = normalizeString(name);
                const selected = rows.find(row => row.id === selectedRow[0]?.id);

                if (selected && normalizeString(selected.name) === normalizedName) {
                    return false; // No marcar como duplicado si es el mismo nombre del registro editado
                }

                return rows.some(row => normalizeString(row.name) === normalizedName);
            } else {
                const normalizedInput = normalizeString(name);
                return rows?.some((row) => normalizeString(row.name) === normalizedInput) || false;
            }
        };

        const checkChanges = (): boolean => {
            // @ts-ignore
            const { general } = zone;

            // Verificar cambios básicos
            const basicChange = general.name !== '' || general.description !== '' || general.requiredApproval !== false;
            if (basicChange) return true;

            return false;
        };

        // ✅ VALIDACIONES ESPECÍFICAS POR TIPO DE ZONA
        // @ts-ignore
        const typeValidation = validateFormByType(zone);

        // @ts-ignore
        const nameExists = existName(zone.general.name);
        // @ts-ignore
        const isDisabled = zone.general.name === '' || nameExists || !typeValidation.isValid;
        const hasChanges = checkChanges();

        return {
            existName: nameExists,
            hasChanges,
            isDisabled
        };
    }, [zone, rows, selectedRow]);
};

export { useFormValidation };
